import { ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { fontSize } from '../../..';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  toggleAxesEdited,
  updateAxesQueryParam,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import {
  setParameterData,
  setParameterIntoFilters,
} from '../../../redux/Parameters/ParametersAction';
import { updateCalculation } from '../../../redux/Calculations/CalculationsActions';
import { chartFilterGroupEdited } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { ICurrentCalculationSession } from '../../../redux/Calculations/CurrentCalculationSessionInterface';
import { ParameterProps } from '../ParameterContainer';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../../redux';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { AxisType } from '../../CommonFunctions/aliases';

const InputField = ({
  parameter,
  handleEditParameter,
  propKey,
  editMode,
  showDash,
  color,
  backgroundColor,
}: ParameterProps) => {
  const { chartProperties, calculations } = useSelector((state: RootState) => {
    return {
      chartProperties: state.chartProperties,
      calculations: state.calculations,
    };
  });
  const dispatch = useDispatch();

  const currentChartAxesRef = useRef<any[]>([]);
  const currentChatFieldRef = useRef<any>(null);
  const currentChartAxesNameRef = useRef<any>(null);
  const itemIndexRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const [inputValue, setInputValue] = useState(() =>
    parameter.userSelection === '(Empty)'
      ? ''
      : parameter.userSelection || parameter?.columns?.defaultValue || '',
  );
  const [savedInputValue, setSavedInputValue] = useState<string | number>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(true);
  const open = Boolean(anchorEl);

  const currentChartField = chartProperties.properties[propKey]?.chartAxes[0]?.fields?.find(
    (field: any) => field.parameterId === parameter.parameterId,
  );

  const dataType = parameter.columns?.dataType;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const callQueryAPIFilter = () => {
    let callAPIDashboard = false;
    chartProperties.propList[1].forEach((tabTileId: string) => {
      chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
        if (field.parameterId === parameter.parameterId) {
          dispatch(callQueryAPI(tabTileId, true));
          callAPIDashboard = true;
        }
      });
      let measureAxes = chartProperties.properties[tabTileId].chartAxes.filter((axis: any) =>
        [AxisType.Measure, AxisType.X, AxisType.Y].includes(axis.name),
      );
      measureAxes.forEach((axes: any) => {
        axes.fields.some((field: any) => {
          if (field.override) {
            field.override[0].fields.some((FilterField: any) => {
              if (FilterField.parameterId === parameter.parameterId) {
                dispatch(callQueryAPI(tabTileId, true));
                callAPIDashboard = true;
              }
            });
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const callAPI = (uid: string | undefined) => {
    let callAPIDashboard = false;
    Object.entries(chartProperties.properties).forEach(([tabTileId, prop]: [string, any]) => {
      prop.chartAxes?.forEach((axis: any) => {
        axis.fields?.forEach((field: any) => {
          if (field.SavedCalculationUUID === uid) {
            dispatch(callQueryAPI(tabTileId, true));
            callAPIDashboard = true;
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const debouncedUpdateFilter = (value: string) => {
    // if the value is same as the saved value then return
    // this is to prevent from updating the chart filter when the value is same
    dispatch(setParameterData(parameter.parameterId, value.trim() || '(Empty)'));

    if (dataType === 'text') {
      setSavedInputValue(value.trim());
      if (value.trim() === savedInputValue) return;
    } else if (dataType === 'number' || dataType === 'decimal') {
      if (parseFloat(value) === savedInputValue) return;
      setSavedInputValue(parseFloat(value));
    }

    // if the parameter is connected with any chart filter fields then update the chart according to the value
    // if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
    //   if (currentChatFieldRef.current.fieldtypeoption === 'Pick List') {
    //     currentChatFieldRef.current.userSelection = value.trim() ? [value.trim()] : [];
    //   } else {
    //     currentChatFieldRef.current.exprInput = value.trim() || '';
    //     if (value.trim() === '') currentChatFieldRef.current.userSelection = [];
    //   }

    //   updateUserFilterItem(
    //     propKey,
    //     0,
    //     itemIndexRef.current,
    //     currentChatFieldRef.current,
    //     currentChartAxesNameRef.current,
    //   );
    // }
    Array.isArray(parameter.chartFilter) &&
      parameter.chartFilter.forEach((cf: any) => {
        if (!cf.currentChartAxesName?.includes('chartAxes_')) {
          if (cf.propKey) {
            const clonedField = {
              ...chartProperties.properties[cf.propKey].chartAxes[0].fields[cf.itemIndex],
            };
            if (clonedField.fieldtypeoption === 'Pick List') {
              clonedField.userSelection = value.trim() ? [value.trim()] : [];
            } else {
              clonedField.exprInput = value.trim() || '';
              if (value.trim() === '') clonedField.userSelection = [];
            }
            updateUserFilterItem(cf.propKey, 0, cf.itemIndex, clonedField, cf.currentChartAxesName);
          } else if (cf.itemIndex >= 0 && cf.currentChartAxesName) {
            chartProperties.propList[1].forEach((tabTileId: string) => {
              chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
                if (field.parameterId === parameter.parameterId) {
                  dispatch(
                    setParameterIntoFilters(
                      tabTileId,
                      parameter.parameterId,
                      field.uId,
                      cf.itemIndex,
                      cf.currentChartAxesName,
                    ),
                  );

                  const clonedField = {
                    ...chartProperties.properties[tabTileId].chartAxes[0].fields[cf.itemIndex],
                  };
                  if (clonedField.fieldtypeoption === 'Pick List') {
                    clonedField.userSelection = value.trim() ? [value.trim()] : [];
                  } else {
                    clonedField.exprInput = value.trim() || '';
                    if (value.trim() === '') clonedField.userSelection = [];
                  }
                  updateUserFilterItem(
                    tabTileId,
                    0,
                    cf.itemIndex,
                    clonedField,
                    cf.currentChartAxesName,
                  );
                }
              });
            });
          }
        }
      }),
      callQueryAPIFilter();

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );

      targetCalculation.map((calculation: ICurrentCalculationSession) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      if (!inputValue) filter.rightOperand[index] = '0';
                      else filter.rightOperand[index] = inputValue.toString();
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          } else {
            let source = targetFlow.source;
            let input: number | string;

            if (dataType === 'number' || dataType === 'decimal') {
              if (parseFloat(value) === 0) {
                input = 0;
              } else if (!value) {
                if (targetFlow.flow === 'division') input = 1;
                else input = 0;
              } else input = parseFloat(value);
            } else if (dataType === 'text') {
              input = value.trim();
            }

            source = source.map((source: any, index: number) => {
              const isParameterized = targetFlow.parameter.find(
                (param: any) =>
                  param.parameterId === parameter.parameterId && param.index === index,
              );
              if (isParameterized) {
                if (typeof input === 'string') {
                  return parseFloat(input);
                }
                return input;
              }
              return source;
            });
            targetFlow.source = source;
            calculation.calculationInfo.flows[flowName][0] = targetFlow;
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
          callAPI(calculation.uuid);
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        if (filter.propKey) {
          const chartAxes = chartProperties.properties[filter.propKey]?.chartAxes;
          const chartFilter = chartAxes[chartAxes.length - 1].fields;
          chartFilter?.forEach((field: any, index: number) => {
            if (currentChartAxesName_ === field.enableOverrideForUID) {
              return;
            }
            if (field.uId === currentChartAxesName_) {
              const override_ = field.override;
              if (Array.isArray(override_) && override_.length > 0) {
                override_[0].fields.forEach((field_: any, index_: number) => {
                  if (field_.uId === filter.chartFilterId) {
                    if (field_.fieldtypeoption === 'Pick List') {
                      field_.userSelection = [value];
                    } else field_.exprInput = value;
                    field.override = override_;
                    updateUserFilterItem(
                      filter.propKey,
                      chartAxes.length - 1,
                      index,
                      field,
                      'chartAxes',
                    );
                    callQueryAPIFilter();
                  }
                });
              }
            }
          });
        } else {
        }
      }
    });
  };

  const handleInputChange = (e: any) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  useEffect(() => {
    currentChartAxesRef.current = chartProperties.properties[propKey]?.chartAxes[0]?.fields || [];

    // Find the field where any chartFilter in parameter.chartFilter matches filter.uId
    currentChatFieldRef.current = currentChartAxesRef.current.find(
      (filter: any) =>
        Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.some((cf: any) => {
          if (cf.chartFilterId === filter.uId) {
            currentChartAxesNameRef.current = cf.currentChartAxesName;
            itemIndexRef.current = cf.itemIndex;
            return cf.chartFilterId === filter.uId;
          }
        }),
    );
  }, [chartProperties.properties[propKey]?.chartAxes[0]?.fields, parameter.chartFilter, propKey]);

  // if the parameter had any default value then it will update the Graph
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (
      currentChatFieldRef.current &&
      currentChatFieldRef.current.isParameterized &&
      inputValue.length > 0
    ) {
      if (currentChatFieldRef.current.fieldtypeoption === 'Pick List') {
        currentChatFieldRef.current.userSelection = [inputValue];
      } else currentChatFieldRef.current.exprInput = inputValue;

      updateUserFilterItem(
        propKey,
        0,
        itemIndexRef.current,
        currentChatFieldRef.current,
        currentChartAxesNameRef.current,
      );
      callQueryAPIFilter();
    }

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      if (!inputValue) filter.rightOperand[index] = '0';
                      else filter.rightOperand[index] = inputValue.toString();
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          } else {
            const targetFlow = calculation.calculationInfo.flows[flowName][0];
            let source = targetFlow.source;
            let input: number | string;

            if (dataType === 'number' || dataType === 'decimal') {
              if (parseFloat(inputValue) === 0) {
                input = 0;
              } else if (!inputValue) {
                if (targetFlow.flow === 'division') input = 1;
                else input = 0;
              } else input = parseFloat(inputValue);
            } else if (dataType === 'text') {
              input = inputValue;
            }
            source = source.map((source: any, index: number) => {
              const isParameterized = targetFlow.parameter.find(
                (param: any) =>
                  param.parameterId === parameter.parameterId && param.index === index,
              );
              if (isParameterized) {
                if (typeof input === 'string') {
                  return parseFloat(input);
                }
                return input;
              }
              return source;
            });
            targetFlow.source = source;
            calculation.calculationInfo.flows[flowName][0] = targetFlow;
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        const chartAxes = chartProperties.properties[propKey]?.chartAxes;
        const chartFilter = chartAxes[chartAxes.length - 1].fields;
        chartFilter?.forEach((field: any, index: number) => {
          if (currentChartAxesName_ === field.enableOverrideForUID) {
            return;
          }
          if (field.uId === currentChartAxesName_) {
            const override_ = field.override;
            if (Array.isArray(override_) && override_.length > 0) {
              override_[0].fields.forEach((field_: any, index_: number) => {
                if (field_.uId === filter.chartFilterId) {
                  if (field_.fieldtypeoption === 'Pick List') {
                    field_.userSelection = [inputValue];
                  } else field_.exprInput = inputValue;
                  field.override = override_;
                  updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                  callQueryAPIFilter();
                }
              });
            }
          }
        });
      }
    });
  }, [
    parameter.chartFilter,
    // currentChatFieldRef.current,
    // propKey,
    // currentChartField?.parameterId
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              transform: 'capitalize',
              color: color,
              textAlign: 'start',
            }}
          >
            {parameter?.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: -1 }}>
          {editMode && (
            <IconButton
              size="small"
              id="basic-button"
              aria-controls={Boolean(anchorEl) ? 'basic-menu' : undefined}
              sx={{ color: color }}
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{
              mr: '0.4rem',
              color: color,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem dense onClick={() => handleEditParameter(parameter.parameterId)}>
          Remove
        </MenuItem>
      </Menu>

      {expanded && (
        <input
          ref={(input) => {
            (input as HTMLInputElement | null)?.setAttribute('data-input', 'true');
          }}
          className="custom-input"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={parameter?.columns?.dataType === 'text' ? 'Enter an item' : 'Enter a value'}
          type={parameter?.columns?.dataType === 'text' ? 'text' : 'number'}
          style={{
            fontSize: fontSize.medium,
            width: '100%',
            padding: '5px 10px',
            color: color,
            background: backgroundColor,
            border: '1px solid',
            borderColor: color || 'rgba(110, 110, 110, 0.5)',
            borderRadius: '4px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              debouncedUpdateFilter(inputValue);
              (e.target as HTMLInputElement).blur();
            }
          }}
          onBlur={() => debouncedUpdateFilter(inputValue)}
        />
      )}
    </Box>
  );
};

export default InputField;
